const API = 'https://api.goinvestors.co.uk/api';

export function getToken() {
  return localStorage.getItem('gi_token');
}
export function setToken(token) {
  localStorage.setItem('gi_token', token);
}
export function clearToken() {
  localStorage.removeItem('gi_token');
}

export const fmt = (v) => {
  const n = parseFloat(v) || 0;
  return '£' + n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB');
};

export const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export async function apiFetch(path, options = {}) {
  const token = getToken();
  const res = await fetch(API + path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (res.status === 401) { clearToken(); window.location.href = '/'; return null; }
  return res.json().catch(() => null);
}

export const api = {
  // Auth
  login: (email, password) => apiFetch('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  me: () => apiFetch('/auth/me'),
  forgotPassword: (email) => apiFetch('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  authResetPassword: (token, password) => apiFetch('/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, password }) }),

  // Franchises
  getFranchises: (directorView) => apiFetch('/franchises' + (directorView ? '?directorView=true' : '')),
  getFranchise: (id) => apiFetch(`/franchises/${id}`),
  createFranchise: (data) => apiFetch('/franchises', { method: 'POST', body: JSON.stringify(data) }),
  updateFranchise: (id, data) => apiFetch(`/franchises/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteFranchise: (id) => apiFetch(`/franchises/${id}`, { method: 'DELETE' }),
  getFranchiseSalaries: (id) => apiFetch(`/franchises/${id}/salaries`),
  updateFranchiseSalaries: (id, data) => apiFetch(`/franchises/${id}/salaries`, { method: 'POST', body: JSON.stringify(data) }),
  addFinancials: (id, data) => apiFetch(`/franchises/${id}/financials`, { method: 'POST', body: JSON.stringify(data) }),
  updateFinancials: (id, month, data) => apiFetch(`/franchises/${id}/financials/${encodeURIComponent(month)}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteFinancials: (id, month) => apiFetch(`/franchises/${id}/financials/${encodeURIComponent(month)}`, { method: 'DELETE' }),
  getInvestorPayments: (franchiseId) => apiFetch(`/franchises/${franchiseId}/investor-payments`),
  saveInvestorPayment: (franchiseId, data) => apiFetch(`/franchises/${franchiseId}/investor-payments`, { method: 'PUT', body: JSON.stringify(data) }),
  getBankHistory: (id) => apiFetch(`/franchises/${id}/bank-details`),
  saveBankDetails: (id, data) => apiFetch(`/franchises/${id}/bank-details`, { method: 'POST', body: JSON.stringify(data) }),
  addFranchiseOwner: (id, data) => apiFetch(`/franchises/${id}/owners/add`, { method: 'POST', body: JSON.stringify(data) }),
  changeShares: (id, data) => apiFetch(`/franchises/${id}/owners/share-change`, { method: 'POST', body: JSON.stringify(data) }),
  changeInvestorShares: (id, data) => apiFetch(`/franchises/${id}/investors/share-change`, { method: 'POST', body: JSON.stringify(data) }),
  addSalary: (id, data) => apiFetch(`/franchises/${id}/salaries`, { method: 'POST', body: JSON.stringify(data) }),

  // Declarations
  getDeclarations: (directorView) => apiFetch('/declarations' + (directorView ? '?directorView=true' : '')),
  getDeclaration: (id) => apiFetch(`/declarations/${id}`),
  submitDeclaration: (data) => apiFetch('/declarations', { method: 'POST', body: JSON.stringify(data) }),
  updateDeclaration: (id, data) => apiFetch(`/declarations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteDeclaration: (id) => apiFetch(`/declarations/${id}`, { method: 'DELETE' }),
  approveDeclaration: (id) => apiFetch(`/declarations/${id}/approve`, { method: 'PUT' }),
  rejectDeclaration: (id, reason) => apiFetch(`/declarations/${id}/reject`, { method: 'PUT', body: JSON.stringify({ reason }) }),
  confirmPayment: (id, paymentMethod, datePaid) => apiFetch(`/declarations/${id}/confirm-payment`, { method: 'PUT', body: JSON.stringify({ paymentMethod: paymentMethod || 'bank_transfer', datePaid: datePaid || new Date().toISOString().split('T')[0] }) }),
  requestEdit: (id, data) => apiFetch(`/declarations/${id}/edit-request`, { method: 'PUT', body: JSON.stringify(data) }),
  approveEditRequest: (id) => apiFetch(`/declarations/${id}/edit-request-approve`, { method: 'PUT' }),
  rejectEditRequest: (id, reason) => apiFetch(`/declarations/${id}/edit-request-reject`, { method: 'PUT', body: JSON.stringify({ reason }) }),
  markAmendmentSettled: (id) => apiFetch(`/declarations/${id}/amendment-settled`, { method: 'PUT' }),
  unsettle: (id) => apiFetch(`/declarations/${id}/amendment-unsettle`, { method: 'PUT' }),

  // Royalties
  getRoyalties: () => apiFetch('/royalty'),
  createRoyalty: (data) => apiFetch('/royalty', { method: 'POST', body: JSON.stringify(data) }),
  syncJotform: () => apiFetch('/royalty/sync', { method: 'POST' }),
  updateRoyalty: (id, data) => apiFetch(`/royalty/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteRoyalty: (id) => apiFetch(`/royalty/${id}`, { method: 'DELETE' }),

  // Profit
  getProfit: (directorView) => apiFetch('/profit' + (directorView ? '?directorView=true' : '')),
  postWithdrawal: (data) => apiFetch('/profit/withdraw', { method: 'POST', body: JSON.stringify(data) }),
  editWithdrawal: (id, data) => apiFetch(`/profit/withdraw/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteWithdrawal: (id) => apiFetch(`/profit/withdraw/${id}`, { method: 'DELETE' }),
  setOpeningBalance: (data) => apiFetch('/profit/opening-balance', { method: 'POST', body: JSON.stringify(data) }),
  deleteOpeningBalance: (data) => apiFetch('/profit/opening-balance', { method: 'DELETE', body: JSON.stringify(data) }),

  // Users
  getUsers: () => apiFetch('/users'),
  createUser: (data) => apiFetch('/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => apiFetch(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  editUser: (id, data) => apiFetch(`/users/${id}/edit`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteUser: (id) => apiFetch(`/users/${id}`, { method: 'DELETE' }),
  resetPassword: (id, password) => apiFetch(`/users/${id}/reset-password`, { method: 'PUT', body: JSON.stringify({ password }) }),
  changePassword: (id, currentPassword, newPassword) => apiFetch(`/users/${id}/change-password`, { method: 'PUT', body: JSON.stringify({ currentPassword, newPassword }) }),
  updatePermissions: (id, permissions) => apiFetch(`/users/${id}/permissions`, { method: 'PUT', body: JSON.stringify({ permissions }) }),
  toggleGotutorsAccess: (id, enable) => apiFetch(`/users/${id}/gotutors-access`, { method: 'PUT', body: JSON.stringify({ enable }) }),
  sendWelcomeEmail: (id) => apiFetch(`/users/${id}/welcome-email`, { method: 'POST' }),

  // Pipeline
  getPipeline: () => apiFetch('/pipeline'),
  createPipeline: (data) => apiFetch('/pipeline', { method: 'POST', body: JSON.stringify(data) }),
  updatePipeline: (id, data) => apiFetch(`/pipeline/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deletePipeline: (id) => apiFetch(`/pipeline/${id}`, { method: 'DELETE' }),

  // Activity
  getActivity: () => apiFetch('/activity'),
  logActivity: (action, details) => apiFetch('/activity', { method: 'POST', body: JSON.stringify({ action, details }) }),
  clearActivity: () => apiFetch('/activity', { method: 'DELETE' }),

  // Compliance
  getCompliance: (userId) => apiFetch(`/compliance/${userId}`),
  uploadCompliance: (userId, docType, formData) => fetch(API + `/compliance/${userId}/${encodeURIComponent(docType)}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: formData,
  }).then(r => r.json()),
  deleteCompliance: (userId, docType) => apiFetch(`/compliance/${userId}/${encodeURIComponent(docType)}`, { method: 'DELETE' }),
  downloadCompliance: (userId, docType) => apiFetch(`/compliance/${userId}/${encodeURIComponent(docType)}/url`),

  // CFO
  getCFODirectors: () => apiFetch('/cfo/directors'),
  updateCFODirectors: (directors) => apiFetch('/cfo/directors', { method: 'PUT', body: JSON.stringify({ directors }) }),
  getCFOPL: () => apiFetch('/cfo/pl-imports'),
  importPL: (data) => apiFetch('/cfo/pl-import', { method: 'POST', body: JSON.stringify(data) }),
  deletePL: (period) => apiFetch(`/cfo/pl-import/${encodeURIComponent(period)}`, { method: 'DELETE' }),
  getCFONotes: () => apiFetch('/cfo/notes'),
  saveCFONote: (period, note) => apiFetch('/cfo/notes', { method: 'POST', body: JSON.stringify({ period, note }) }),
  getCFOAccountMappings: () => apiFetch('/cfo/account-mappings'),

  // Xero
  getXeroStatus: () => apiFetch('/xero/status'),
  getXeroLog: () => apiFetch('/xero/log'),
  createXeroInvoice: (data) => apiFetch('/xero/invoice', { method: 'POST', body: JSON.stringify(data) }),
  createXeroInvoiceForRoyalty: (data) => apiFetch('/xero/invoice-for-royalty', { method: 'POST', body: JSON.stringify(data) }),
  retryXeroInvoice: (id) => apiFetch(`/xero/log/${id}/retry`, { method: 'POST' }),
  deleteXeroLog: (id) => apiFetch(`/xero/log/${id}`, { method: 'DELETE' }),
  createInvestorPayment: (data) => apiFetch('/square/create-investor-payment', { method: 'POST', body: JSON.stringify(data) }),
  refreshXeroAccounts: () => apiFetch('/xero/refresh-accounts'),
};

